import { useState, useMemo, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import type { SortingState } from '@tanstack/react-table';
import type { TopAdData } from '../ads-table-types';

/**
 * Hook for managing state and logic for the Social Ads Top Ads Table.
 *
 * @param data - The data array for the table
 * @param isLoading - Whether the table is loading
 * @param isRefreshing - Whether the table is refreshing
 * @returns {Object} An object containing adsTableProps for the table component, plus state and setters.
 */
export function useSocialAdsTopAdsTable({
  data,
  isLoading = false,
  isRefreshing = false,
}: {
  data: TopAdData[];
  isLoading?: boolean;
  isRefreshing?: boolean;
}) {
  const location = useLocation();
  const history = useHistory();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchValue, setSearchValue] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const pageSize = 50;

  // Get current page from URL or default to 1
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  // Update URL when page changes
  const setCurrentPage = useCallback(
    (page: number) => {
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.set('page', page.toString());
      history.replace({ pathname: location.pathname, search: newSearchParams.toString() });
    },
    [history, location.pathname, location.search],
  );

  // Filter data based on search
  const filteredData = useMemo(() => {
    if (!searchValue.trim()) return data;
    const searchLower = searchValue.toLowerCase();
    return data.filter((ad) => {
      const adName = ad.by && 'name' in ad.by && typeof ad.by.name === 'string' ? ad.by.name : '';
      const socialAccount = ad.by && 'id' in ad.by && typeof ad.by.id === 'string' ? ad.by.id : '';
      // Convert ROAS to formatted string that matches display format (e.g., "4.20X")
      const roasFormatted = ad.metrics && typeof ad.metrics.roas === 'number' ? `${ad.metrics.roas.toFixed(2)}X` : '';
      return (
        adName.toLowerCase().includes(searchLower) ||
        socialAccount.toLowerCase().includes(searchLower) ||
        roasFormatted.toLowerCase().includes(searchLower)
      );
    });
  }, [data, searchValue]);

  // Calculate record range for display
  const startRecord = useMemo(() => (currentPage - 1) * pageSize + 1, [currentPage, pageSize]);
  const endRecord = useMemo(
    () => Math.min(currentPage * pageSize, filteredData.length),
    [currentPage, pageSize, filteredData.length],
  );

  // Paginate data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, pageSize]);

  // Reset to first page when search changes
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);
      setCurrentPage(1);
    },
    [setCurrentPage],
  );

  // Calculate total pages
  const totalPages = Math.ceil(filteredData.length / pageSize);

  const handlePreviousPage = useCallback(() => {
    setCurrentPage(Math.max(currentPage - 1, 1));
  }, [currentPage, setCurrentPage]);

  const handleNextPage = useCallback(() => {
    setCurrentPage(Math.min(currentPage + 1, totalPages));
  }, [currentPage, totalPages, setCurrentPage]);

  const handleExport = useCallback(async (_type: 'current' | 'full') => {
    setIsExporting(true);
    try {
      // TODO: Implement actual export functionality
      await new Promise((resolve) => setTimeout(resolve, 1000));
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  }, []);

  const adsTableProps = {
    data: paginatedData,
    isLoading,
    isRefreshing,
    sorting,
    setSorting,
    searchValue,
    setSearchValue,
    currentPage,
    setCurrentPage,
    isExporting,
    setIsExporting,
    pageSize,
    filteredData,
    totalPages,
    startRecord,
    endRecord,
    handleSearchChange,
    handlePreviousPage,
    handleNextPage,
    handleExport,
  };

  return {
    adsTableProps,
    sorting,
    setSorting,
    searchValue,
    setSearchValue,
    currentPage,
    setCurrentPage,
    isExporting,
    setIsExporting,
    pageSize,
    filteredData,
    totalPages,
    startRecord,
    endRecord,
    handleSearchChange,
    handlePreviousPage,
    handleNextPage,
    handleExport,
  };
}
